'use strict'
// FUENTE ÚNICA de humanización de claves internas → español nítido (voz PrimeLab · compañero culto).
// Evita que enums del motor (muscular_quads, general_fitness) se fuguen crudos al pixel.

const MUSCLE_LABELS = {
  quads: 'cuádriceps', glutes: 'glúteos', hamstrings: 'isquiotibiales',
  calves: 'gemelos', core: 'core', abs: 'abdomen', back: 'espalda', chest: 'pecho',
  shoulders: 'hombros', biceps: 'bíceps', triceps: 'tríceps', forearms: 'antebrazos',
  full_body: 'todo el cuerpo', legs: 'tren inferior', upper: 'tren superior',
  rear_deltoids: 'deltoides posterior', spinal_erectors: 'espinales', lats: 'dorsales',
  traps: 'trapecios', adductors: 'aductores', abductors: 'abductores', hip_flexors: 'flexores de cadera',
}

// quita prefijos (muscular_, muscular_soreness_) y sufijos de lateralidad (_left/_right/_izquierdo/_derecho)
function muscleLabel(zone) {
  if (!zone || typeof zone !== 'string') return zone
  let k = zone.toLowerCase()
    .replace(/^muscular_soreness_/, '').replace(/^muscular_/, '').replace(/^soreness_/, '')
    .replace(/_(left|right|izquierdo|derecho|l|r)$/, '')
  return MUSCLE_LABELS[k] || k.replace(/_/g, ' ')
}

// une una lista de zonas humanizadas y DEDUPLICA (quads_left + quads_right → un solo "cuádriceps")
function muscleList(zones, sep = ', ') {
  const seen = new Set()
  const out = []
  for (const z of (Array.isArray(zones) ? zones : [])) {
    const l = muscleLabel(z)
    if (l && !seen.has(l)) { seen.add(l); out.push(l) }
  }
  return out.join(sep)
}

// REGISTRO INLINE (minúscula · para prosa "compañero culto": "tu plan de ganar músculo").
// Consumido por landingPresenter/goalFit/Day1Mode(_GOAL_NOUN). NO title-case aquí a propósito.
const GOAL_LABELS = {
  hypertrophy: 'ganar músculo', build_muscle: 'ganar músculo', gain_muscle: 'ganar músculo', muscle_gain: 'ganar músculo',
  body_composition: 'composición corporal', recompose: 'recomposición', recomp: 'recomposición',
  lose_fat: 'pérdida de grasa', fat_loss: 'pérdida de grasa',
  running: 'carrera de fondo', marathon: 'maratón', half_marathon: 'media maratón',
  longevity: 'longevidad', health_longevity: 'longevidad', health_and_longevity: 'longevidad',
  strength: 'fuerza', general_fitness: 'salud general', general: 'salud general',
  improve_health: 'salud general', improve_endurance: 'resistencia',
  sport_performance: 'rendimiento deportivo', performance: 'rendimiento deportivo',
  performance_specific: 'rendimiento específico',
  rehabilitation: 'recuperación activa', rehab_return: 'recuperación activa', return_to_sport: 'volver al deporte',
  recover_form: 'recuperar la forma', habit_change: 'cambio de hábitos', habits: 'hábitos sostenibles',
  start_training: 'empezar a entrenar', start_running: 'empezar a correr', event_date: 'llegar fino a un evento',
}
function goalLabel(goal) {
  if (!goal || typeof goal !== 'string') return goal
  return GOAL_LABELS[goal] || goal.replace(/_/g, ' ')
}

// REGISTRO STANDALONE (Title-case · para chip/heading/Row: "Maratón", "Carrera de fondo").
// FUENTE ÚNICA de los mapas antes dispersos en TodayV2Page/Day1Mode/plan-progress/PerfilPL/PerfilPagePL.
// Copy canónico = el de TodayV2 (la superficie mantenida · CABLE-FIX). El goalTitle acepta subtype
// (más específico gana). Divergencias de copy previas (recompose/longevidad/endurance) → COPY_PENDIENTE_CATA.
const GOAL_TITLES = {
  longevity:            'Longevidad',
  health_and_longevity: 'Longevidad',
  health_longevity:     'Longevidad',
  marathon:             'Maratón',
  half_marathon:        'Media maratón',
  // COLA:el-objetivo-se-pierde-camino-del-progreso · el 10K estaba en el CHECK y en el selector, y sin
  // título propio caía al `replace(/_/g,' ')` del final: la pantalla decía «10k».
  '10k':                'Diez kilómetros',
  body_composition:     'Composición corporal',
  sport_performance:    'Rendimiento deportivo',
  rehabilitation:       'Recuperación activa',
  rehab_return:         'Recuperación activa',
  return_to_sport:      'Volver al deporte',
  recover_form:         'Recuperar la forma',
  habits:               'Hábitos sostenibles',
  running:              'Carrera de fondo',
  improve_endurance:    'Mejorar la resistencia',
  event_date:           'Llegar fino a un evento',
  start_training:       'Empezar a entrenar',
  habit_change:         'Cambio de hábitos',
  general_fitness:      'Salud general',
  general:              'Salud general',
  recompose:            'Recomposición',
  lose_fat:             'Pérdida de grasa',
  fat_loss:             'Pérdida de grasa',
  build_muscle:         'Ganar músculo',
  gain_muscle:          'Ganar músculo',
  muscle_gain:          'Ganar músculo',
  strength:             'Fuerza',
  hypertrophy:          'Ganar músculo',
  // COLA:prime-enum-crudo · el hito final del arco de recuperación (clave 'prime'): nunca se pinta la clave (texto a cata)
  prime:                'Tu mejor forma',
  prime_protocol:       'Protocolo Prime',
  prime_essential:      'Prime Esencial',
  performance_specific: 'Rendimiento específico',
  start_running:        'Empezar a correr',
}
// subtypes de evento/carrera (más específicos que el goal base).
const GOAL_SUBTYPE_TITLES = {
  marathon: 'Maratón', half_marathon: 'Media maratón', '10k': 'Carrera 10k', '5k': 'Carrera 5k', wedding: 'Boda',
}
/** Etiqueta STANDALONE del objetivo (Title-case). subtype gana sobre goal. Fallback honesto (nunca enum crudo). */
function goalTitle(goal, subtype = null) {
  if (subtype && GOAL_SUBTYPE_TITLES[subtype]) return GOAL_SUBTYPE_TITLES[subtype]
  if (goal && GOAL_TITLES[goal]) return GOAL_TITLES[goal]
  if (!goal || typeof goal !== 'string') return 'Tu objetivo'
  return goal.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())
}

// fases (menstrual/periodización) → español nítido · defensivo por si alguna superficie pinta la clave cruda
const PHASE_LABELS = {
  fase_folicular: 'fase folicular', follicular: 'fase folicular', fase_lutea: 'fase lútea', luteal: 'fase lútea',
  late_luteal: 'fase lútea tardía', ovulation: 'ovulación', ovulacion: 'ovulación', menstrual: 'menstruación',
  fase_peak: 'fase de pico', fase_taper: 'fase de afinamiento', adaptation: 'adaptación', intensification: 'intensificación',
  deload: 'descarga', peak: 'pico',
}
function phaseLabel(ph) {
  if (!ph || typeof ph !== 'string') return ph
  return PHASE_LABELS[ph.toLowerCase()] || ph.replace(/^fase_/, 'fase ').replace(/_/g, ' ')
}

// COLA:hub-lee-un-entreno-viejo-como-hoy · el tipo interno de actividad (strength_training) y el nivel (beginner) se
// escapaban crudos a la lectura del Hub. Mapa único en prosa (minúscula): lo lee el presentador y el intérprete.
const ACTIVITY_LABELS = {
  running_easy: 'rodaje', running_long: 'tirada larga', running_tempo: 'rodaje tempo',
  running_interval: 'sesión de series', strength_training: 'sesión de fuerza',
  cycling_easy: 'rodaje en bicicleta', cardio_easy: 'cardio', cardio_hiit: 'HIIT',
  cardio_zone2: 'rodaje zona 2', sport_padel: 'partido de pádel',
  sport_football: 'partido de fútbol', sport_tennis: 'partido de tenis', sport_other: 'partido',
  active_recovery: 'sesión de recuperación', mobility: 'sesión de movilidad',
  yoga: 'yoga', walk: 'caminata', generic_activity: 'entreno', stretching: 'estiramientos',
}
function activityLabel(type) {
  return ACTIVITY_LABELS[type] || 'sesión'
}
const LEVEL_LABELS = { beginner: 'principiante', intermediate: 'intermedio', advanced: 'avanzado' }
function levelLabel(level) {
  return LEVEL_LABELS[level] || null
}

module.exports = { muscleLabel, muscleList, goalLabel, goalTitle, phaseLabel, activityLabel, levelLabel, MUSCLE_LABELS, GOAL_LABELS, GOAL_TITLES, GOAL_SUBTYPE_TITLES, PHASE_LABELS, ACTIVITY_LABELS, LEVEL_LABELS }
